import {Bell, X} from 'lucide-react'
import React, {useState, useCallback, useMemo, useRef, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {
  CANDIDATE_UNREAD_QUERY,
  getCandidateUserIds,
  isCandidateRelevantNotification,
} from '~/src/components/notifications/candidateNotifications'
import {getNotificationDestination} from '~/src/components/notifications/notificationDestination'
import {useInvitationNotificationAccess} from '~/src/components/notifications/useInvitationNotificationAccess'
import {useOpenNotificationDestination} from '~/src/components/notifications/useOpenNotificationDestination'
import {useSettingsRowPermission} from '~/src/components/platform/settings/settingsPermission'
import {useSwitchableAgencies} from '~/src/hooks/useSwitchAgency'
import {SIDEBAR_ROUTES} from '~/src/routes/constants'
import type {Notification} from '~/src/models/notification'
import {useCurrentUser} from '~/src/store/slice-hooks'
import {useAppSelector} from '~/src/store/store-hooks'
import {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useLazyGetNotificationsQuery,
  useMarkAsReadMutation,
} from '~/src/store/apis/notifications-api'
import {
  BellBadge,
  BellWrapper,
  CloseButton,
  NotificationText,
  PopoverContainer,
  PopoverHeader,
  PopoverTitle,
  ViewAllButton,
} from '../../tasks/styles/TasksPageStyles'
import NotificationCard from './NotificationCard'

interface NotificationBellProps {
  variant?: string
}

const MAX_PREVIEW_COUNT = 3

const PopoverPositioner = styled.div`
  position: absolute;
  top: calc(100% + var(--space-2));
  right: 0;
  z-index: var(--z-dropdown);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-elevated);
`

function isCandidateRole(currentUser: unknown): boolean {
  if (!currentUser || typeof currentUser !== 'object') return false
  const roles = (currentUser as {roles?: unknown}).roles
  if (!Array.isArray(roles)) return false
  return roles.some((role) => {
    const normalizedRole = String(role).toLowerCase()
    return normalizedRole === 'candidate' || normalizedRole === 'ollicandidate'
  })
}

const NotificationBell: React.FC<NotificationBellProps> = ({variant}) => {
  const navigate = useNavigate()
  const {currentUser} = useCurrentUser()
  const [open, setOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)

  const {canView: canViewWorkAuthorization} = useSettingsRowPermission('work_authorization')
  const {canOpenPendingOrders, canOpenJobOrders} = useInvitationNotificationAccess()
  // STFFLG-4184: an invitee's invitation sent to another of their branches selects it first.
  const loggedInAgencyId = useAppSelector((state) => state.root.loggedInAgencyId)
  const switchableAgencies = useSwitchableAgencies()
  const switchableAgencyIds = useMemo(
    () => switchableAgencies.map((agency) => agency.id),
    [switchableAgencies],
  )
  const openDestination = useOpenNotificationDestination()

  const isCandidateUser = isCandidateRole(currentUser)
  const candidateUserIds = useMemo(() => getCandidateUserIds(currentUser), [currentUser])

  const {data: unreadData, refetch: refetchUnread} = useGetUnreadCountQuery(undefined, {
    skip: isCandidateUser,
    pollingInterval: 60000,
  })
  const {data: candidateNotificationsData, refetch: refetchCandidateNotifications} =
    useGetNotificationsQuery(CANDIDATE_UNREAD_QUERY, {skip: !isCandidateUser})

  const [triggerGetNotifications, {data: notificationsData}] = useLazyGetNotificationsQuery()
  const [markAsRead] = useMarkAsReadMutation()

  const candidateNotifications = useMemo(
    () =>
      (candidateNotificationsData?.data ?? []).filter((notification) =>
        isCandidateRelevantNotification(notification, candidateUserIds),
      ),
    [candidateNotificationsData?.data, candidateUserIds],
  )
  const unreadCount = isCandidateUser ? candidateNotifications.length : unreadData?.count ?? 0
  const notifications = useMemo(
    () =>
      isCandidateUser
        ? candidateNotifications.slice(0, MAX_PREVIEW_COUNT)
        : notificationsData?.data ?? [],
    [candidateNotifications, isCandidateUser, notificationsData?.data],
  )
  const totalNotifications = isCandidateUser
    ? candidateNotifications.length
    : notificationsData?.total ?? 0
  const markedIds = useRef<Set<string>>(new Set())

  const markVisibleAsRead = useCallback(() => {
    const unread = notifications.filter((n) => !n.isRead && !markedIds.current.has(n.id))
    if (unread.length === 0) return
    unread.forEach((n) => markedIds.current.add(n.id))
    void Promise.all(unread.map((n) => markAsRead(n.id)))
  }, [notifications, markAsRead])

  const handleOpen = useCallback(() => {
    markedIds.current.clear()
    setOpen(true)
    if (isCandidateUser) {
      void refetchCandidateNotifications()
    } else {
      void refetchUnread()
      void triggerGetNotifications({page: 1, limit: MAX_PREVIEW_COUNT, isRead: false})
    }
  }, [isCandidateUser, refetchCandidateNotifications, refetchUnread, triggerGetNotifications])

  const handleClose = useCallback(() => {
    markVisibleAsRead()
    setOpen(false)
  }, [markVisibleAsRead])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!popoverRef.current?.contains(e.target as Node)) handleClose()
    }
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', escHandler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', escHandler)
    }
  }, [open, handleClose])

  // Where each type goes lives in getNotificationDestination, shared with the inbox. Null = the
  // card has no action (a withdrawn invitation, or a page this role cannot open).
  const destinationOf = useCallback(
    (notification: Notification) =>
      getNotificationDestination(notification, {
        variant,
        canViewWorkAuthorization,
        canOpenPendingOrders,
        canOpenJobOrders,
        loggedInAgencyId,
        switchableAgencyIds,
      }),
    [
      variant,
      canViewWorkAuthorization,
      canOpenPendingOrders,
      canOpenJobOrders,
      loggedInAgencyId,
      switchableAgencyIds,
    ],
  )

  const handleOpenTask = useCallback(
    (notification: Notification) => {
      const destination = destinationOf(notification)
      if (!destination) return
      if (!notification.isRead && !markedIds.current.has(notification.id)) {
        markedIds.current.add(notification.id)
        void markAsRead(notification.id)
      }
      handleClose()
      // Selects the invitation's branch first when the destination names one.
      void openDestination(destination)
    },
    [handleClose, markAsRead, destinationOf, openDestination],
  )

  const handleViewAll = useCallback(() => {
    handleClose()
    navigate(SIDEBAR_ROUTES.NOTIFICATIONS)
  }, [navigate, handleClose])

  return (
    <div ref={popoverRef} style={{position: 'relative', display: 'inline-flex'}}>
      <BellWrapper onClick={handleOpen} aria-label="Notifications" data-testid="notification-bell">
        <Bell size={20} />
        {unreadCount > 0 && (
          <BellBadge data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </BellBadge>
        )}
      </BellWrapper>

      {open && (
        <PopoverPositioner>
          <PopoverContainer
            data-testid="notification-popover"
            style={notifications.length === 0 ? {maxHeight: 'none'} : undefined}>
            <PopoverHeader>
              <PopoverTitle>Notifications</PopoverTitle>
              <CloseButton onClick={handleClose} aria-label="Close notifications">
                <X size={20} />
              </CloseButton>
            </PopoverHeader>

            {notifications.length === 0 ? (
              <>
                <NotificationText
                  style={{
                    textAlign: 'center',
                    color: 'var(--neutral-600)',
                    padding: 'var(--space-6) 0',
                  }}>
                  {isCandidateUser ? 'No notifications available' : 'No unread notifications'}
                </NotificationText>
                {!isCandidateUser && (
                  <ViewAllButton onClick={handleViewAll} data-testid="view-all-button">
                    View all notifications
                  </ViewAllButton>
                )}
              </>
            ) : (
              <>
                {notifications.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    onOpenTask={destinationOf(notification) ? handleOpenTask : undefined}
                  />
                ))}
                {!isCandidateUser && (
                  <ViewAllButton onClick={handleViewAll} data-testid="view-all-button">
                    {totalNotifications > MAX_PREVIEW_COUNT
                      ? `View all ${
                          totalNotifications > 20 ? '20+' : totalNotifications
                        } notifications`
                      : 'View all notifications'}
                  </ViewAllButton>
                )}
              </>
            )}
          </PopoverContainer>
        </PopoverPositioner>
      )}
    </div>
  )
}

export default NotificationBell
